(ns minorodata.components.pipeline-editor.dag
  "xyflow canvas for the pipeline editor — the scoped authoring view of one anchor's
   neighbourhood.

   Node cards, cluster boxes and the canvas context menu are shared with the global
   pipelines page (`minorodata.components.pipeline-dag.*`), so an entity looks and
   reads the same on both. The zoom-out collapse to status squares is NOT used here —
   it earns its keep on the global page's 1000-node graph, not on a scoped one.

   What's local here is the authoring half: click previews into the details pane
   below, the hover `+` affordances that add an input table / a downstream transform,
   the unsaved-changes badge, and the sandbox delete / reset actions."
  (:require
   ["lucide-react" :refer [Check ChevronRight Code Folder Loader2 PanelLeftClose
                           PanelLeftOpen PanelRightClose PanelRightOpen Plus
                           Settings WandSparkles Waypoints]]
   ["react" :as react]
   [applied-science.js-interop :as jsi]
   [bleene.dag.context-menu :refer [launch-transform]]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :refer [assoc-some]]
   [minorodata.components.pipeline-dag.menu :as pd.menu]
   [minorodata.components.pipeline-dag.nodes :as pd.nodes]
   [minorodata.components.pipeline-editor.utils :refer [features-context
                                                        navigate-analytics-model-tab
                                                        navigate-extract-tab
                                                        navigate-tab
                                                        navigate-to-extract-edit
                                                        navigate-to-extract-legacy
                                                        navigate-transform-tab
                                                        refresh-live-button
                                                        tab-icon]]
   [minorodata.components.sql-console.utils :refer [node-type type-icon]]
   [minorodata.components.transformations.utils :refer [show-transform-delete-modal]]
   [minorodata.statuses.domain :refer [extract-status->ui]]
   [minorodata.sql-console.api :as sql-console.api]
   [minorodata.utils :refer [find-first]]
   [mold-forms.core :as form-component :refer [model->fields]]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :as button]
   [ui.dag :as ui.dag]
   [ui.dropdown-menu :as dropdown-menu]
   [ui.fields :as fields]
   [ui.popover :as popover]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn next-object [object]
  (case object
    "catalogs" "schemas"
    "schemas"  "tables"
    "tables"   nil))

(defn metadata-dropdown-content [{:keys [id object catalog schema]}]
  (let [params  (assoc-some {:object object}
                            :catalog catalog
                            :schema  schema)
        objects @(re-posh/subscribe [:sql-metadata-partial/prop
                                     [:sql-metadata-partial/params params]
                                     :sql-metadata-partial/metadata])
        loader-key       (sql-console.api/->metadata-request-id params)
        loading?         @(re-posh/subscribe [:mold-http/loading-id? :sql-console.api/fetch-metadata loader-key])]
    (react/useEffect
     (fn []
       (when (empty? objects)
         (dispatch [:sql-console.api/fetch-metadata {:object object
                                                     :catalog catalog
                                                     :schema  schema}]))
       js/undefined)
     #js [])

    [:<>
     (when (and loading? (nil? objects))
       [:div
        [:> Loader2 {:class "animate-spin text-kleene-primary-v2"}]])

     (when (and (not loading?) (some? objects) (empty? objects))
       [:div {:class "text-kleene-text-secondary text-sm p-2"}
        "No " object " found."])

     (if (= object "tables")
       (for [o objects
             :let [icon (type-icon (node-type o))]]
         [dropdown-menu/dropdown-menu-item
          {:key (:table-name o)
           :on-click #(dispatch [:pipeline-editor.events/add-table-to-transform
                                 {:id id
                                  :table (:table-name o)
                                  :catalog (or (:table-cat o) catalog)
                                  :schema  (or (:table-schem o) schema)}])}
          [:> icon {:class "text-kleene-text-secondary mr-2" :size 16}]
          (:table-name o)])
       (for [o objects
             :let [icon (type-icon (node-type o))]]
         [dropdown-menu/dropdown-menu-sub
          {:key (str (:table-cat o) "-" (:table-schem o))
           :content [:f> metadata-dropdown-content {:id      id
                                                    :object  (next-object object)
                                                    :catalog (or (:table-cat o) catalog)
                                                    :schema  (or (:table-schem o) schema)}]}
          [:> icon {:class "text-kleene-text-secondary mr-2" :size 16}]
          [:span {:class "flex items-center gap-2 justify-between w-full"}
           (or (:table-schem o) (:table-cat o))
           [:> ChevronRight {:size 16}]]]))]))

(defn preview-tab-href [{:keys [type id group-id opened]}]
  (rt.easy/href :pipeline-editor {} (when-not opened
                                      {:tab-preview-type     type
                                       :tab-preview-id       id
                                       :tab-preview-group-id group-id})))


(defn- preview!
  "Single click in the editor previews the entity in the details pane below rather
   than opening a tab, by writing the `tab-preview-*` query params.

   `tab` asks that pane to open a specific tab (the context menu's `Preview → Logs`,
   see `node-menu-items`). It is dispatched on every preview, so an ordinary click
   also clears a tab a previous one asked for.

   A plain click TOGGLES: clicking a node whose tab is already open clears the
   preview instead of re-previewing it. Asking for a tab does not — `Preview → Logs`
   is a named action, and on an already-open node the toggle would have closed the
   pane rather than showing the logs, or (when a different tab was active) landed the
   request on whatever node that pane was showing."
  ([data type]
   (preview! data type nil))
  ([{:keys [id group-id opened]} type tab]
   (let [clear? (and opened (nil? tab))]
     (dispatch [:pipeline-editor.events/request-details-tab {:id id :tab tab}])
     (dispatch [:app.events/navigate-query
                {:tab-preview-type     (when-not clear? type)
                 :tab-preview-id       (when-not clear? id)
                 :tab-preview-group-id (when-not clear? group-id)}]))))


;; =============================================================================
;; Transform nodes — separate live vs sandbox wrappers
;; =============================================================================

(defn- add-input-table-affordance
  "Hover-revealed `+` button (feature-flagged) that adds an input table to the
   transform via the metadata dropdown."
  [{:keys [data disabled]}]
  [:div {:title "Add input table"
         :class "absolute top-1/2 left-2 -translate-x-full -translate-y-1/2 z-20 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto cursor-pointer"}
   [dropdown-menu/dropdown-menu-root
    [dropdown-menu/dropdown-menu-trigger
     [button/button {:component :div
                     :size     :icon-sm
                     :icon     Plus
                     :class    "border border-black/20"
                     :disabled disabled}]]
    [dropdown-menu/dropdown-menu-content
     [:f> metadata-dropdown-content {:id (:id data)
                                     :object "catalogs"}]]]])

(defn transform-node-shell
  "Shared scaffold for live and sandbox transform nodes: the entity card (click →
   preview, double-click → open tab), an optional unsaved `badge?`, the
   add-input-table affordance (feature-flagged), and the error/blocked tooltip.
   Right-click is handled once at the canvas (see `node-menu-items`); the live vs
   sandbox split lives in `state` / `badge?`, supplied by the caller. `read-only?`
   strips the authoring half — see the read-only canvas section below."
  [{:keys [data state badge? read-only?]}]
  (let [{:keys [id title opened status error-message disabled]} data
        add-input-table? (and (not read-only?)
                              @(re-posh/subscribe [:pipeline-editor.subs/enabled-feature :add-input-table]))
        loading?         @(re-posh/subscribe [:pipeline-editor.subs/tab-loading?])]
    [tooltip/tooltip-root
     [ui.dag/node-group
      [tooltip/tooltip-trigger {:as-child true}
       (pd.nodes/entity-card
        {:data data
         :root {:disabled?       disabled
                :loading?        loading?
                :state           state
                :on-click        (when-not read-only?
                                   #(preview! data :transform))
                :on-double-click (when-not read-only?
                                   #(when (and (not opened) (not loading?))
                                      (navigate-transform-tab id (:group-id data) title)))}})]

      (when badge?
        [:div {:title "This transform has unsaved changes"
               :class "absolute -top-1.5 -right-1.5 border border-kleene-text-warning bg-kleene-warning-02 text-white text-xs h-3 w-3 flex items-center justify-center rounded-full"}])

      (when add-input-table?
        [add-input-table-affordance {:data data :disabled disabled}])]

     (when (or error-message (= status "blocked"))
       [tooltip/tooltip-content {:class "text-xs overflow-auto bg-kleene-dark-01 text-kleene-secondary-05 max-w-4xl w-full"}
        (cond
          error-message       [:pre [:code error-message]]
          (= status "blocked") "Upstream transform(s) not finished or failed")])]))

(defn live-transform-node' [{:keys [data read-only?]}]
  [transform-node-shell {:data data :read-only? read-only?}])

(defn sandbox-transform-node' [{:keys [data]}]
  (let [{:keys [id]} data
        local-command @(re-posh/subscribe [:pipeline-transform-unsaved/prop [:pipeline-transform-unsaved/id id]
                                           :pipeline-transform-unsaved/command])
        command       @(re-posh/subscribe [:pipeline-transform/prop [:pipeline-transform/id id]
                                           :pipeline-transform/command])
        state         @(re-posh/subscribe [:pipeline-editor.subs/transform-state id])
        unsaved?      (and (not-empty local-command)
                           (not= local-command command))]
    [transform-node-shell {:data   data
                           :state  state
                           :badge? unsaved?}]))

(defn extract-node' [{:keys [data read-only?]}]
  (let [{:keys [id group-id]} data
        ;; The status bar respects `:last-run-row?` too, or the card would colour a
        ;; run whose date the layout is hiding.
        run-row?                (get data :last-run-row? true)
        loading?                @(re-posh/subscribe [:pipeline-editor.subs/tab-loading?])
        extract-details-drawer? @(re-posh/subscribe [:unleash/is-enabled "fe.extract-details-drawer"])
        ;; Falls back to the id already carried on the DAG payload (populated from the
        ;; dependency graph, may be stale after an edit) while the connections store
        ;; hasn't been warmed up for this source yet — see details/extracts.cljs, which
        ;; is what warms it. Without this, a node renders with no double-click
        ;; affordance at all until some other extract in the source has been previewed.
        active-extract-id       (or @(re-posh/subscribe [:connections.subs/active-extract-id id])
                                    (get-in data [:metadata :extract-id]))
        ;; Run state comes off the extract ENTITY first — the `:extract/last-run-*`
        ;; pair the details sheet reads, so the two cannot disagree — and falls back
        ;; to the graph-wide status list, which is all a cold load has. Pulled, not
        ;; `/prop`-ed: both keys are undeclared on the `extract` model.
        extract                 (when (and run-row? active-extract-id)
                                  @(re-posh/subscribe
                                    [:extract/pull [:extract/extract-id active-extract-id]]))
        entity-status           (:extract/last-run-status extract)
        listed                  (when (and run-row? active-extract-id (nil? entity-status))
                                  @(re-posh/subscribe
                                    [:statuses.subs/extract-status active-extract-id]))
        status                  (if entity-status
                                  (extract-status->ui entity-status)
                                  (:status listed))
        run-at                  (or (:extract/last-run-at extract)
                                    (get-in listed [:last-run :completed-at]))]
    [ui.dag/node-group
     (pd.nodes/entity-card
      (cond-> {:data data
               :root {:loading?        loading?
                      :on-click        (when-not read-only?
                                         #(preview! data :extract))
                      ;; Double-click edits the extract rather than opening a tab —
                      ;; the tab is still reachable from the context menu.
                      :on-double-click (when-not read-only?
                                         #(when (and (not loading?) active-extract-id)
                                            (if extract-details-drawer?
                                              (navigate-to-extract-edit group-id active-extract-id)
                                              (navigate-to-extract-legacy group-id active-extract-id))))}}
        ;; Left off entirely rather than passed as nil, so `entity-card`'s
        ;; `(get props :status (:status data))` fallback stays reachable.
        (or status run-at)
        (assoc :status   status
               ;; No `:started-at`: an extract reports a run date but no start time,
               ;; so `execution-time` renders the status label + date, no duration.
               :last-run {:status status :completed-at run-at})))]))

(defn analytics-model-node' [{:keys [data read-only?]}]
  (let [{:keys [id group-id title opened]} data
        ;; The graph node is keyed by the model's master-id, but its instance page is
        ;; addressed by the per-version model-id (carried in metadata). Stash it in
        ;; the generic per-type preview slot so the details panel can link to the
        ;; instance without adding model-specific keys to the query (FT-2575).
        ;; `:status`/`:last-run` are joined in by enrich-node using the same keys as
        ;; transforms, so the shared card renders the same status bar (FT-2574).
        model-id (get-in data [:metadata :model-id])
        loading? @(re-posh/subscribe [:pipeline-editor.subs/tab-loading?])]
    [ui.dag/node-group
     (pd.nodes/entity-card
      {:data data
       :root {:loading?        loading?
              :on-click        (when-not read-only?
                                 #(dispatch [:app.events/navigate-query
                                             {:tab-preview-type             (when-not opened :model)
                                              :tab-preview-id               (when-not opened id)
                                              :tab-preview-group-id         (when-not opened group-id)
                                              :tab-preview-node-metadata-id (when-not opened model-id)}]))
              :on-double-click (when-not read-only?
                                 #(when (and (not opened) (not loading?))
                                    (navigate-analytics-model-tab id group-id title)))}})]))

(defn new-transform-name-suggestion [id]
  (when id
    (let [number (some-> (re-find #"\d+$" id) (js/parseInt 10))]
      (if number
        (str/replace id #"\d+$" (str (inc number)))
        (str id "_OUTPUT")))))

(defn new-transform-form' [{:keys [input-table on-create transform groups show-sql-generator?]}]
  (let [[clicked set-clicked] (react/useState false)
        mode-ref              (react/useRef "sql")]
    [:div
     [form-component/form
      (->form-model
       {:id        :new-transform-node-form
        :validate  (fn [{:keys [transform-name group-id new-group]}]
                     (not-empty
                      (cond-> {}
                        (str/blank? transform-name)
                        (assoc :transform-name ["Transform name is required."])
                        (or (and (str/blank? group-id)
                                 (str/blank? new-group))
                            (and (= "new-group" group-id)
                                 (str/blank? new-group)))
                        (assoc :group-id ["Please select a group or create a new one."]))))
        :on-submit (fn [form-state]
                     (let [{:keys [group-id transform-name new-group]} (model->fields form-state)
                           mode (keyword (jsi/get mode-ref :current))
                           cb #(on-create {:group-id %
                                           :name     transform-name
                                           :mode     mode})]
                       (set-clicked true)
                       (if (not-empty new-group)
                         (dispatch [:pipeline-editor.events/create-group
                                    {:group-name new-group
                                     :on-success cb}])
                         (cb group-id))))
        :fields    [{:name  :transform-name
                     :value (or (some-> (new-transform-name-suggestion input-table) (str/split #"\.") last) "")}
                    {:name  :group-id
                     :value (or (:pipeline-transform/group-id transform) "")
                     :process identity}
                    {:name :new-group
                     :value ""}]})
      (fn [fields errors on-submit]
        [:form {:class "flex flex-col gap-2 w-110"
                :on-submit on-submit}
         [fields/input {:label "Transform name"
                        :errors (get errors :transform-name)
                        :field (:transform-name fields)
                        :required true}]
         [fields/select {:label "Group"
                         :id     :group-id
                         :native false
                         :errors errors
                         :field (get fields :group-id)
                         :required true
                         :placeholder "Select group"
                         :options (concat
                                   [{:value "new-group"
                                     :text [:div {:class "flex items-center gap-2"}
                                            [:> Plus {:size 16}]
                                            "Create new group"]}
                                    {:type :separator :key "separator"}]
                                   (mapv (fn [{:pipeline-group/keys [id name]}]
                                           {:value id :text name})
                                         groups))}]
         (when (= "new-group" (get-in fields [:group-id :value]))
           [fields/input {:label "New group name"
                          :errors errors
                          :id :new-group
                          :field (get fields :new-group)
                          :required true}])
         [:div {:class (clsx "flex items-end gap-2 mt-2"
                             (if show-sql-generator? "*:flex-1 items-center" "justify-end"))}
          (when show-sql-generator?
            [:<>
             [button/button {:type     :submit
                             :variant  :empty
                             :icon     WandSparkles
                             :on-click #(jsi/assoc! mode-ref :current "sql-generator")
                             :loading  clicked
                             :disabled clicked
                             :icon-props {:size 20 :class "text-kleene-secondary-v2"}
                             :data-cy  "create-with-sql-generator-btn"}
              [:div
               [ui.typography/h4 "SQL Generator"]
               [ui.typography/small {:class "text-kleene-text-secondary"} "Automated JSON parsing and flattening"]]]
             "or"])
          [button/button {:type     :submit
                          :variant  :empty
                          :on-click #(jsi/assoc! mode-ref :current "sql")
                          :icon     Code
                          :loading  clicked
                          :disabled clicked
                          :icon-props {:size 20 :class "text-kleene-primary-v2"}
                          :data-cy  "create-with-sql-btn"}
           (if show-sql-generator?
             [:div
              [ui.typography/h4 "SQL Editor"]
              [ui.typography/small {:class "text-kleene-text-secondary"} "Standard SQL query editor"]]
             "Create")]]])]]))

(defn new-transform-form [props]
  (let [transform            @(re-posh/subscribe [:pipeline-editor.subs/transform-with-output-table (:input-table props)])
        groups               @(re-posh/subscribe [:pipeline-editor.subs/transform-groups])
        warehouse-type       @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        sql-generator-flag?  @(re-posh/subscribe [:pipeline-editor.subs/enabled-feature "sql-generator"])
        show-sql-generator?  (and sql-generator-flag? (= "snowflake" warehouse-type))]
    [:f> new-transform-form' (assoc props
                                    :transform           transform
                                    :groups              groups
                                    :show-sql-generator? show-sql-generator?)]))

(defn add-transform-popover [{:keys [data]}]
  (let [{:keys [disabled]} data
        [popover set-popover] (react/useState false)]
    [:div {:class "absolute top-1/2 right-2 translate-x-full -translate-y-1/2 z-20 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto cursor-pointer"}
     [popover/popover-root {:open popover
                            :on-open-change set-popover}
      [popover/popover-trigger
       [button/button {:title "Add transform"
                       :component :div
                       :size    :icon-sm
                       :icon    Plus
                       :class   "border border-black/20"
                       :disabled disabled}]]
      [popover/popover-content
       [typography/h4 {:class "mb-4"} "Create new transform"]
       [new-transform-form
        {:input-table (:id data)
         :on-create (fn [{:keys [group-id name mode]}]
                      (set-popover false)
                      (if (= :sql-generator mode)
                        (dispatch [:app.events/navigate-query
                                   {:sql-generator                true
                                    :sql-generator-group-id       group-id
                                    :sql-generator-transform-name name
                                    :sql-generator-lock-table     true
                                    :table-name                   (:id data)}])
                        (let [ev (if @(re-posh/subscribe [:sandbox.subs/enabled?])
                                   :pipeline-editor.events/create-and-select-transform-sandbox
                                   :pipeline-editor.events/create-and-select-transform-with-table)]
                          (dispatch [ev {:group-id       group-id
                                         :transform-name name
                                         :input-table    (:id data)}]))))}]]]]))

(defn- table-node-base
  "Shared table-node body — the compact icon-only pill (the name lives in the
   tooltip) that previews on click / opens a tab on double-click, with an optional
   `add-transform?` hover popover (sandbox only)."
  [{:keys [data add-transform? read-only?]}]
  (let [{:keys [id group-id title type opened]} data]
    [ui.dag/node-group
     [tooltip/tooltip
      {:content [pd.nodes/table-tooltip-content {:data data}]}
      (pd.nodes/table-card
       {:data  data
        :shape :pill
        :root  {:href            (when-not read-only? (preview-tab-href data))
                :on-double-click (when (and (not read-only?) (not opened))
                                   #(navigate-tab {:id       id
                                                   :group-id group-id
                                                   :type     type
                                                   :name     title
                                                   :preview  false}))}})]
     (when add-transform?
       [:f> add-transform-popover {:data data}])]))

(defn live-table-node' [{:keys [data read-only?]}]
  [table-node-base {:data data :add-transform? false :read-only? read-only?}])

(defn sandbox-table-node' [{:keys [data]}]
  ;; The feature flag is read through the subscription rather than
  ;; `features-context`: node bodies are rendered as plain reagent components by
  ;; `pd.nodes/entity-node` (so their re-posh subscriptions stay reactive), which
  ;; rules out React hooks like `useContext` here. Same value either way — the
  ;; context provider is seeded from these features (see `pipeline-editor.core`).
  (let [add-transform? @(re-posh/subscribe [:pipeline-editor.subs/enabled-feature :add-transform])]
    [table-node-base {:data data :add-transform? add-transform?}]))


;; Extract and model nodes behave identically in live and sandbox, so both maps share
;; them; only transforms and tables get sandbox-specific wrappers. Container (group /
;; source) box types are merged in unconditionally — they only appear in the graph when
;; the "Groups" filter is on (see `minorodata.pipeline-dag.layout/do-layout`).
(defn- node-types [entity-types]
  (js/Object.assign #js {} entity-types pd.nodes/container-node-types))

(def live-node-types
  (node-types
   #js {:transform (pd.nodes/entity-node live-transform-node')
        :extract   (pd.nodes/entity-node extract-node')
        :table     (pd.nodes/entity-node live-table-node')
        :model     (pd.nodes/entity-node analytics-model-node')}))

(def sandbox-node-types
  (node-types
   #js {:transform (pd.nodes/entity-node sandbox-transform-node')
        :extract   (pd.nodes/entity-node extract-node')
        :table     (pd.nodes/entity-node sandbox-table-node')
        :model     (pd.nodes/entity-node analytics-model-node')}))

;; A read-only canvas — the pipeline preview embedded in the extract details drawer
;; (see `connections.extract-pipeline-preview`) — renders the same cards with the
;; authoring half stripped: no preview click, no tab on double-click, no hover
;; affordances, and no right-click menu (see `subdag`). It is decided once at the
;; canvas from `features-context` and baked into these node types, because node
;; bodies are plain reagent components and so can't read the context themselves
;; (see `pd.nodes/entity-node` and `sandbox-table-node'` below).
(defn- read-only-node [inner]
  (pd.nodes/entity-node (fn [props] [inner (assoc props :read-only? true)])))

(def read-only-node-types
  (node-types
   #js {:transform (read-only-node live-transform-node')
        :extract   (read-only-node extract-node')
        :table     (read-only-node live-table-node')
        :model     (read-only-node analytics-model-node')}))


;; =============================================================================
;; Canvas context menu
;; =============================================================================
;;
;; One menu for every node, opened at the cursor (see `pd.menu`). Items come from the
;; shared vocabulary so an action that also exists on the global pipelines page reads
;; identically; what is editor-specific is the authoring half — delete a sandbox
;; transform, reset its unsaved command — and the fact that "open" means an editor
;; tab rather than an anchored tab on a page.

(defn- transform-menu-items
  [{:keys [id title group-id metadata] :as data} {:keys [can-write? can-read-logs?]}]
  (let [transform-id  (:transform-id metadata)
        sandbox?      @(re-posh/subscribe [:sandbox.subs/enabled?])
        failure?      (= "failed" (:status data))
        local-command @(re-posh/subscribe [:pipeline-transform-unsaved/prop
                                           [:pipeline-transform-unsaved/id id]
                                           :pipeline-transform-unsaved/command])
        command       @(re-posh/subscribe [:pipeline-transform/prop
                                           [:pipeline-transform/id id]
                                           :pipeline-transform/command])
        unsaved?      (and (not-empty local-command) (not= local-command command))]
    (into
     [(pd.menu/section "Actions")
      ;; Sandbox transforms can't be run individually — the sandbox runs as a unit.
      (when-not sandbox?
        (pd.menu/run-item {:can-write? can-write?
                           :on-run     #(launch-transform transform-id group-id % id)}))
      (pd.menu/open-tab-item
       {:label    "Open in tab"
        :on-click #(navigate-transform-tab id group-id title)})
      (pd.menu/kai-item {:master-id id :group-id group-id :title title
                         :failure?  failure? :sandbox? sandbox?})]
     ;; Everything below Actions splits on the mode, because a sandbox transform has
     ;; no live counterpart to look at: its logs would be the LIVE transform's, and
     ;; Settings / SQL Console both address the live record by `transform-id` (which a
     ;; transform created in the sandbox doesn't have yet). What takes their place is
     ;; the authoring half — reset unsaved changes, delete from the sandbox.
     (if sandbox?
       [(pd.menu/section "Sandbox")
        (when unsaved?
          (pd.menu/reset-changes-item
           {:on-click #(dispatch [:pipeline-editor.events/purge-local-transform id])}))
        (pd.menu/delete-item
         {:on-click #(show-transform-delete-modal {:id              id
                                                   :group-id        group-id
                                                   :pipeline-editor true})})]
       [(pd.menu/section "Preview")
        ;; Previews the node in the details pane below — same as a left click on the
        ;; card — and opens its Logs tab there, rather than navigating away to the
        ;; global logs screen (which is what the global pipelines page does).
        (pd.menu/logs-item
         {:disabled? (not can-read-logs?)
          :on-click  #(preview! data :transform "logs")})
        (pd.menu/section "Open in")
        (pd.menu/settings-item
         {:on-click #(dispatch [:app.events/navigate :transform-details
                                {:group-id group-id :transform-id transform-id}])})
        (pd.menu/sql-console-item {:transform-id transform-id :can-write? can-write?})]))))

(defn- extract-menu-items
  [{:keys [id title group-id metadata] :as data} {:keys [can-read-logs?]}]
  ;; The node is keyed by the extract's master-id, but its settings page is addressed
  ;; by the extract-id — same resolution (and same staleness caveat) as the node's
  ;; double-click, see `extract-node'`.
  (let [extract-details-drawer? @(re-posh/subscribe [:unleash/is-enabled "fe.extract-details-drawer"])
        active-extract-id       (or @(re-posh/subscribe [:connections.subs/active-extract-id id])
                                    (:extract-id metadata))]
    [(pd.menu/section "Actions")
     (pd.menu/open-tab-item {:label    "Open in tab"
                             :on-click #(navigate-extract-tab id group-id title)})
     (pd.menu/section "Preview")
     ;; As for transforms: preview in the pane below on the Logs tab, not the global
     ;; logs screen.
     (pd.menu/logs-item {:disabled? (not can-read-logs?)
                         :on-click  #(preview! data :extract "logs")})
     (pd.menu/section "Open in")
     (pd.menu/settings-item
      {:disabled? (not active-extract-id)
       :on-click  #(when active-extract-id
                     (if extract-details-drawer?
                       (navigate-to-extract-edit group-id active-extract-id)
                       (navigate-to-extract-legacy group-id active-extract-id)))})]))

(defn- model-menu-items [{:keys [id title group-id metadata]}]
  (let [model-id (:model-id metadata)]
    [(pd.menu/section "Actions")
     (pd.menu/open-tab-item {:label    "Open in tab"
                             :on-click #(navigate-analytics-model-tab id group-id title)})
     (pd.menu/section "Open in")
     (pd.menu/settings-item
      {:on-click #(dispatch [:app.events/navigate :analytics-model-instance-details
                             {:instance-id (or model-id id)}])})]))

(defn- table-menu-items [{:keys [id title group-id type]}]
  [(pd.menu/section "Actions")
   (pd.menu/open-tab-item {:label    "Open in tab"
                           :on-click #(navigate-tab {:id       id
                                                     :group-id group-id
                                                     :type     type
                                                     :name     title
                                                     :preview  false})})])

(defn- node-menu-items
  "Menu items for the right-clicked node, dispatched on its type. `perms` carries the
   once-resolved `:can-write?` / `:can-read-logs?` from `pd.menu/graph-context-menu`."
  [{:keys [type] :as data} perms]
  (case type
    "transform" (transform-menu-items data perms)
    "extract"   (extract-menu-items data perms)
    "model"     (model-menu-items data)
    "table"     (table-menu-items data)
    []))

(def ^:private dag-direction-options
  [["all" "All"] ["upstream" "Upstream"] ["downstream" "Downstream"]])

(defn sidebar-toggle
  "Left-edge affordance for the editor's entity sidebar. Stays top-left because it
   belongs to the sidebar it opens — the mirror of the global page keeping its summary
   toggle on the right."
  []
  (let [collapsed? @(re-posh/subscribe [:pipeline-editor.subs/left-sidebar-collapsed?])]
    (when @(re-posh/subscribe [:pipeline-editor.subs/enabled-feature :sidebar])
      [:div {:class "-ml-2 p-0.5 bg-kleene-background rounded-lg shadow-md rounded-l-none border"}
       [button/button {:icon       (if collapsed? PanelLeftOpen PanelLeftClose)
                       :variant    :empty-transparent
                       :size       :icon
                       :icon-props {:size 24}
                       :title      (if collapsed? "Expand sidebar" "Collapse sidebar")
                       :data-cy    :toggle-pe-sidebar
                       :on-click   #(dispatch [:pipeline-editor.events/toggle-left-sidebar])}]])))

(defn summary-toggle
  "Right-edge mirror of `sidebar-toggle`, for the sandbox summary pane. The empty tab
   has no canvas, so it renders its own copy (see `pipeline-editor.panel`)."
  []
  (let [collapsed? @(re-posh/subscribe [:pipeline-editor.subs/right-sidebar-collapsed?])]
    (when @(re-posh/subscribe [:sandbox.subs/enabled?])
      [:div {:class "-mr-2 p-0.5 bg-kleene-background rounded-lg shadow-md rounded-r-none border"}
       [button/button {:icon       (if collapsed? PanelRightOpen PanelRightClose)
                       :variant    :empty-transparent
                       :size       :icon
                       :icon-props {:size 24}
                       :title      (if collapsed? "Expand summary" "Collapse summary")
                       :on-click   (fn []
                                     (when collapsed?
                                       (dispatch [:pipeline-editor.events/reset-right-sidebar-view]))
                                     (dispatch [:pipeline-editor.events/toggle-right-sidebar]))}]])))

(defn view-options
  "Graph view options behind a cog, mirroring the global pipelines page's settings
   menu: a `Show` section of visibility checkboxes first, then the editor-specific
   `Scope` section (lineage + direction) that a scoped view needs and a global one
   doesn't. Changing any of these persists the filters and re-runs layout (see
   :sandbox.events/relayout-dag)."
  [{:keys [filters apply-filters]}]
  (let [direction    (:direction filters "all")
        focused?     (get filters :focused? true)
        show-tables? (get filters :show-tables? true)
        groups?      (get filters :groups? false)
        ;; normalised map so toggling one control preserves the others
        current      {:direction    direction
                      :focused?     focused?
                      :show-tables? show-tables?
                      :groups?      groups?}]
    [dropdown-menu/dropdown-menu-root
     [dropdown-menu/dropdown-menu-trigger
      [button/button {:component  :div
                      :icon       Settings
                      :variant    :empty-transparent
                      :size       :icon
                      :icon-props {:size 24}
                      :title      "Graph view options"
                      :data-cy    :pe-dag-filter}]]
     [dropdown-menu/dropdown-menu-content {:align "end" :class "min-w-52"}
      [dropdown-menu/dropdown-menu-label "Show"]
      [dropdown-menu/dropdown-menu-checkbox
       {:checked           show-tables?
        :icon              Check
        :data-cy           :pe-dag-show-tables
        :on-select         #(.preventDefault %)
        :on-checked-change #(apply-filters (assoc current :show-tables? %))}
       [:span {:class "flex items-center gap-2"}
        [tab-icon {:type "table"}] "Tables"]]
      ;; Same clustering the global pipelines page shows, off by default here: a scoped
      ;; graph usually spans few groups, and each card already names its own.
      [dropdown-menu/dropdown-menu-checkbox
       {:checked           groups?
        :icon              Check
        :data-cy           :pe-dag-show-groups
        :on-select         #(.preventDefault %)
        :on-checked-change #(apply-filters (assoc current :groups? %))}
       [:span {:class "flex items-center gap-2"}
        [:> Folder {:size 16}] "Groups"]]

      [dropdown-menu/dropdown-menu-separator {:class "my-1"}]
      [dropdown-menu/dropdown-menu-label "Scope"]
      [dropdown-menu/dropdown-menu-checkbox
       {:checked           focused?
        :icon              Check
        :data-cy           :pe-dag-focus-lineage
        :on-select         #(.preventDefault %)
        :on-checked-change #(apply-filters (assoc current :focused? %))}
       [:span {:class "flex items-center gap-2"}
        [:> Waypoints {:size 16 :class "text-kleene-text-secondary"}] "Lineage"]]
      [dropdown-menu/dropdown-menu-group
       [dropdown-menu/dropdown-menu-label "Direction"]
       [dropdown-menu/dropdown-menu-radio-group
        {:value           direction
         :on-value-change #(apply-filters (assoc current :direction %))}
        (for [[value text] dag-direction-options]
          ^{:key value}
          [dropdown-menu/dropdown-menu-radio-item
           {:value     value
            :data-cy   (str "pe-dag-direction-" value)
            :on-select #(.preventDefault %)}
           text])]]]]))

(defn- toolbar
  "Top-right action cluster, styled like the global pipelines page's: the live-data
   refresh grouped with the view-options cog behind one border."
  [{:keys [filters apply-filters]}]
  (let [sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    [:div {:class (clsx "p-0.5 bg-kleene-background rounded-lg shadow-md border transition-opacity"
                        {"pointer-events-none opacity-50" (empty? filters)})}
     ;; Sandbox refresh lives in the sandbox summary sidebar instead.
     (when-not sandbox?
       [refresh-live-button])
     [view-options {:filters filters :apply-filters apply-filters}]]))

(defn subdag
  "Rendered via `[:f> subdag …]` by every editor tab, so the right-click target
   (`{:node :x :y}`) — local state for this canvas, driving the single
   `pd.menu/graph-context-menu` — lives in `useState` rather than a reagent atom."
  [{:keys [dag-data apply-filters actions show-controls? show-zoom? min-zoom max-zoom]
    :or   {show-controls? true
           show-zoom?     true
           ;; matches the global pipelines page: a scoped graph can still be wide (a
           ;; transform with dozens of upstream deps), and 0.2 clamped the zoom before
           ;; the whole lineage fit on screen.
           min-zoom       0.05
           max-zoom       1}}]
  (let [[ctx-target set-ctx-target] (react/useState nil)
        dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])
        sandbox?   @(re-posh/subscribe [:sandbox.subs/enabled?])
        ;; Read here rather than in the node bodies: this is the one React function
        ;; component in the canvas, so it's the only place `useContext` is available
        ;; (see `read-only-node-types`).
        read-only? (true? (jsi/get (react/useContext features-context) :read-only?))
        nodes      (:nodes dag-data)
        opened-id  (:id (find-first #(some-> % :data :opened) nodes))
        ;; refit when the node SET changes (a direction / lineage / tables / groups
        ;; toggle reshapes the graph) but not on same-node updates like a status
        ;; refresh — same token trick the global pipelines page uses.
        node-set-token (react/useMemo #(hash (into #{} (map :id) nodes)) #js [nodes])]
    [:<>
     [ui.dag/dag {:nodes      nodes
                  :edges      (:edges dag-data)
                  :node-types (cond
                                read-only? read-only-node-types
                                sandbox?   sandbox-node-types
                                :else      live-node-types)
                  :dark-mode? dark-mode?
                  :min-zoom   min-zoom
                  :max-zoom   max-zoom
                  ;; No `:collapse-below-zoom`: the editor's graph is scoped to one
                  ;; anchor, so it stays legible zoomed out — collapsing cards to
                  ;; status squares only pays off on the global page's 1000-node graph.
                  :only-render-visible? true
                  ;; one context menu for every node (see `pd.menu/graph-context-menu`),
                  ;; and none at all on a read-only canvas — every item authors or
                  ;; navigates away from the surface embedding it.
                  :on-node-context-menu (when-not read-only?
                                          (pd.menu/on-context-menu set-ctx-target))}
      [ui.dag/background {:dark? dark-mode?}]
      [ui.dag/viewport-controller {:focus-node-id opened-id :fit-token node-set-token}]
      (when show-zoom?
        [ui.dag/overlay {:position :bottom-left}
         [ui.dag/zoom-controls {:focus-node-id opened-id}]])
      (when show-controls?
        [ui.dag/overlay {:position :top-left}
         [sidebar-toggle]])
      ;; Top-right holds the actions + the options cog, as on the global pipelines
      ;; page. `actions` is honoured even with the controls hidden, so a caller that
      ;; only wants its own buttons still gets them.
      (when (or show-controls? actions)
        [ui.dag/overlay {:position :top-right :class "flex items-center gap-2"}
         actions
         (when show-controls?
           [:<>
            [toolbar {:filters       (:filters dag-data)
                      :apply-filters apply-filters}]
            [summary-toggle]])])]
     (when-not read-only?
       [pd.menu/graph-context-menu {:target   ctx-target
                                    :items    node-menu-items
                                    :on-close #(set-ctx-target nil)}])]))
